import { z } from 'zod';
import { getExposureSpawnPositions } from '../../../../../data/maps';

const performanceSchema = z.object({
  map: z.string().min(1, { message: 'Seleziona uno scenario' }),
  currentState: z
    .string()
    .min(1, { message: 'Seleziona uno stato di partenza' }),
  variation: z
    .number()
    .min(0, { message: 'La variazione non può essere negativa' }),
  duration: z
    .number()
    .min(1, { message: 'La durata deve essere almeno 1 secondo' }),
});

const exposureSchema = z
  .object({
    map: z.string().min(1, { message: 'Seleziona uno scenario' }),
    itemIds: z.array(z.number()),
    multiItem: z.boolean().default(false),
    hasTutorial: z.boolean().default(false),
    userSpawnPosition: z.string().default('1'),
  })
  .superRefine((data, ctx) => {
    const spawnOptions = getExposureSpawnPositions(data.map);
    if (
      spawnOptions.length > 0 &&
      !spawnOptions.some(opt => opt.value === data.userSpawnPosition)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['userSpawnPosition'],
        message: 'Seleziona la posizione di spawn',
      });
    }

    const hasValidId = (id: number) =>
      id != null && Number.isFinite(id) && id !== 0;
    if (data.multiItem) {
      const GROUP_SIZE = 4;
      const flat = data.itemIds;
      if (flat.length < GROUP_SIZE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itemIds'],
          message:
            'In modalità multipla inserisci almeno un gruppo (da 1 a 4 elementi per gruppo).',
        });
        return;
      }
      const groups: number[][] = [];
      for (let i = 0; i < flat.length; i += GROUP_SIZE) {
        groups.push(flat.slice(i, i + GROUP_SIZE));
      }
      while (
        groups.length > 0 &&
        groups[groups.length - 1].every(s => !hasValidId(s))
      ) {
        groups.pop();
      }
      if (groups.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itemIds'],
          message:
            'In modalità multipla inserisci almeno un gruppo (da 1 a 4 elementi per gruppo).',
        });
        return;
      }
      if (flat.length % GROUP_SIZE !== 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itemIds'],
          message:
            'In modalità multipla il numero di slot deve essere un multiplo di 4 (gruppi da 4 slot).',
        });
        return;
      }
      const hasGroupWithoutItem = groups.some(
        g => !g.some(s => hasValidId(s))
      );
      if (hasGroupWithoutItem) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itemIds'],
          message:
            'Ogni gruppo deve avere almeno un elemento selezionato (da 1 a 4 per gruppo).',
        });
      }
    } else {
      if (!data.itemIds.length || data.itemIds.every(id => !hasValidId(id))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itemIds'],
          message: 'Seleziona almeno un elemento.',
        });
      }
    }
  });

const bodyImageSchema = z.object({});

const baseScenarioSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
});

const performanceScenarioSchema = baseScenarioSchema.extend({
  scenarioType: z.literal('performance'),
  performance: performanceSchema,
  exposure: exposureSchema.partial().optional(),
});

const exposureScenarioSchema = baseScenarioSchema.extend({
  scenarioType: z.literal('exposure'),
  exposure: exposureSchema,
  performance: performanceSchema.partial().optional(),
});

const bodyImageScenarioSchema = baseScenarioSchema.extend({
  scenarioType: z.literal('body_image'),
  bodyImage: bodyImageSchema.default({}),
  exposure: exposureSchema.partial().optional(),
  performance: performanceSchema.partial().optional(),
});

export const scenarioConfigSchema = z.discriminatedUnion('scenarioType', [
  performanceScenarioSchema,
  exposureScenarioSchema,
  bodyImageScenarioSchema,
]);

export type ScenarioConfigFormValues = z.infer<typeof scenarioConfigSchema>;
export type ScenarioPerformanceConfig = z.infer<typeof performanceSchema>;
export type ScenarioExposureConfig = z.infer<typeof exposureSchema>;
export type ScenarioBodyImageConfig = z.infer<typeof bodyImageSchema>;
